import React from 'react'
import Text from '../../components/text/Text'
import WizTag from '../../components/tag/WizTag'
import {IProbeReport} from './types'

interface IProps {
    className?: string
    report: IProbeReport
}

const Field: React.FC<{label: string; children: React.ReactNode}> = ({label, children}) => (
    <div className={'space-y-[2px]'}>
        <Text className={'text-ti3 text-font03'}>{label}</Text>
        <div className={'text-body2 text-font01 break-all'}>{children}</div>
    </div>
)

// 리포트 머리말: 어느 키를 언제 얼마나 돌렸는지 + 이 실행 자체를 의심해야 할 이유(warnings).
const ProbeSummary: React.FC<IProps> = ({className, report}) => {
    return (
        <div className={`bg-white rounded-[10px] p-[30px] shadow-normal ${className}`}>
            <div className={'grid grid-cols-4 gap-[20px] md:grid-cols-2 sm:grid-cols-1'}>
                <Field label={'Exchange'}>{report.exchange || '-'}</Field>
                <Field label={'Key ID'}>{report.key_id}</Field>
                <Field label={'User ID'}>{report.user_id || '-'}</Field>
                <Field label={'Account ID'}>{report.account_id || '-'}</Field>
                <Field label={'Key Status'}>{report.status || '-'}</Field>
                <Field label={'Cycle'}>{report.cycle_ms}ms</Field>
                <Field label={'Exchange Registered'}>
                    <WizTag state={report.exchange_found}>{report.exchange_found ? 'Yes' : 'No'}</WizTag>
                </Field>
                <Field label={'Unattributed Requests'}>{report.unattributed_requests ?? 0}</Field>
            </div>

            {/* 등록된 거래소 구현이 없으면 어떤 phase도 돌지 않았다 — 아래 결과는 전부 빈 값이다. */}
            {!report.exchange_found && (
                <div className={'mt-[20px] rounded-[8px] border border-line01 bg-bg_gray01 px-[16px] py-[12px]'}>
                    <Text className={'text-ti2 text-font01'}>이 프로세스에 거래소 구현이 등록돼 있지 않습니다</Text>
                    <Text className={'text-body3 text-font02 mt-[4px]'}>
                        라이브 호출이 하나도 나가지 않았습니다. 거래소 레지스트리가 로드된 서버(cmd/api)에서 다시
                        실행하세요.
                    </Text>
                </div>
            )}

            {/* socket_driven이면 REST가 초록불이어도 sync가 읽는 스트림은 죽어 있을 수 있다. 가장 오해하기 쉬운 지점. */}
            {report.socket_driven && (
                <div className={'mt-[20px] rounded-[8px] border border-tag_yellow bg-bg_golden px-[16px] py-[12px]'}>
                    <Text className={'text-ti2 text-font01'}>
                        socket-driven 거래소 — 깨끗한 결과가 정상을 의미하지 않습니다
                    </Text>
                    <Text className={'text-body3 text-font02 mt-[4px]'}>
                        이 거래소의 sync 경로는 웹소켓을 소비하지만 probe는 REST를 칩니다. 여기가 전부 통과해도 sync
                        루프가 실제로 읽는 소켓은 끊겨 있을 수 있습니다. Key Test의 balance.fresh와 교차 확인하세요.
                    </Text>
                </div>
            )}

            {report.warnings?.length > 0 && (
                <div className={'mt-[20px] space-y-[6px]'}>
                    {report.warnings.map((warning, idx) => (
                        <Text key={idx} className={'text-body3 text-font02'}>
                            · {warning}
                        </Text>
                    ))}
                </div>
            )}

            {/* 0이 아니면 이 실행에 다른 키의 트래픽이 섞여 관측됐다는 뜻. 기록이 비어 있는 이유를 여기서 가른다. */}
            {report.unattributed_requests > 0 && (
                <Text className={'text-body3 text-font03 mt-[16px]'}>
                    실행 중 다른 키에 속한 요청 {report.unattributed_requests}건이 관측됐습니다(바디는 기록되지 않음).
                    아래 목록이 비어 있다면 기록이 안 된 게 아니라 귀속에 실패한 것일 수 있습니다.
                </Text>
            )}
        </div>
    )
}

export default ProbeSummary
